import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Phone, MapPin, ArrowUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const TEXT = {
  tagline: {
    az: 'Bakıda onlayn qadın geyim butiki. Seçilmiş parçalar, məhdud sayda model.',
    en: 'An online womenswear boutique in Baku. Selected fabrics, limited runs.',
    ru: 'Онлайн-бутик женской одежды в Баку. Отобранные ткани, ограниченные партии.',
    tr: 'Bakü de online kadın giyim butiği. Seçilmiş kumaşlar, sınırlı sayıda model.',
  },
  shop: { az: 'Mağaza', en: 'Shop', ru: 'Магазин', tr: 'Mağaza' },
  help: { az: 'Kömək', en: 'Help', ru: 'Помощь', tr: 'Yardım' },
  contact: { az: 'Əlaqə', en: 'Contact', ru: 'Контакты', tr: 'İletişim' },
  online: { az: 'Onlayn mağaza', en: 'Online store', ru: 'Интернет-магазин', tr: 'Online mağaza' },
  rights: {
    az: 'Bütün hüquqlar qorunur',
    en: 'All rights reserved',
    ru: 'Все права защищены',
    tr: 'Tüm hakları saklıdır',
  },
  toTop: { az: 'Yuxarı', en: 'Top', ru: 'Наверх', tr: 'Yukarı' },
  delivery: { az: 'Çatdırılma', en: 'Delivery', ru: 'Доставка', tr: 'Teslimat' },
  products: { az: 'Məhsullar', en: 'Products', ru: 'Товары', tr: 'Ürünler' },
  about: { az: 'Haqqımızda', en: 'About', ru: 'О нас', tr: 'Hakkımızda' },
  blog: { az: 'Bloq', en: 'Blog', ru: 'Блог', tr: 'Blog' },
  wishlist: { az: 'Seçilmişlər', en: 'Wishlist', ru: 'Избранное', tr: 'Favoriler' },
  whatsappOrder: {
    az: 'WhatsApp ilə sifariş',
    en: 'Order via WhatsApp',
    ru: 'Заказ через WhatsApp',
    tr: 'WhatsApp ile sipariş',
  },
};

const Footer = () => {
  const { language } = useLanguage();
  const tx = (obj) => obj[language] || obj.az;
  const year = new Date().getFullYear();

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const linkClass =
    'block text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200 py-1.5';

  const columnTitle = 'text-[11px] tracking-[0.2em] text-gray-900 mb-4';

  return (
    <footer className="bg-white border-t border-gray-100">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Əsas hissə */}
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-x-6 gap-y-10 py-16 sm:py-20">
          {/* Brend */}
          <div className="col-span-2 lg:col-span-4 lg:pr-12">
            <img
              src="/laliel-logo.png"
              alt="L'Aliel"
              className="h-9 w-auto object-contain mb-5"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              {tx(TEXT.tagline)}
            </p>

            <a
              href="https://www.instagram.com/laliel.az"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 text-sm text-gray-900 border-b border-gray-300 hover:border-gray-900 pb-0.5 transition-colors"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
              @laliel.az
            </a>
          </div>

          {/* Mağaza */}
          <div className="lg:col-span-2 lg:col-start-6">
            <h3 className={columnTitle}>{tx(TEXT.shop)}</h3>
            <Link to="/" className={linkClass}>
              {tx(TEXT.products)}
            </Link>
            <a href="/#about" className={linkClass}>
              {tx(TEXT.about)}
            </a>
            <Link to="/wishlist" className={linkClass}>
              {tx(TEXT.wishlist)}
            </Link>
            <Link to="/blog" className={linkClass}>
              {tx(TEXT.blog)}
            </Link>
          </div>

          {/* Kömək */}
          <div className="lg:col-span-2">
            <h3 className={columnTitle}>{tx(TEXT.help)}</h3>
            <Link to="/catdirilma" className={linkClass}>
              {tx(TEXT.delivery)}
            </Link>
            <Link to="/elaqe" className={linkClass}>
              {tx(TEXT.contact)}
            </Link>
            <a
              href="https://wa.me/994999737000"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {tx(TEXT.whatsappOrder)}
            </a>
          </div>

          {/* Əlaqə */}
          <div className="col-span-2 lg:col-span-3">
            <h3 className={columnTitle}>{tx(TEXT.contact)}</h3>

            <a
              href="tel:+994999737000"
              className="flex items-start gap-2.5 text-sm text-gray-500 hover:text-gray-900 transition-colors py-1.5"
            >
              <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              +994 99 973 70 00
            </a>

            <div className="flex items-start gap-2.5 text-sm text-gray-500 py-1.5">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              {tx(TEXT.online)}
            </div>
          </div>
        </div>

        {/* Alt zolaq */}
        <div className="border-t border-gray-100 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs text-gray-400 tracking-wide">
            © {year} L'Aliel. {tx(TEXT.rights)}.
          </p>

          <button
            type="button"
            onClick={scrollTop}
            className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-900 transition-colors self-start sm:self-auto"
          >
            {tx(TEXT.toTop)}
            <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
